/**
 * useBackgroundGeo — browser-native geolocation for PWA preview.
 *
 * Uses navigator.geolocation.watchPosition (works in Chrome/Safari).
 * For native Capacitor builds, swap the watchPosition call for
 * @capacitor-community/background-geolocation once that package's exports
 * are fixed and you're building for Android/iOS.
 *
 * Geofence exit detection (transfer stations) is approximated here by
 * checking distance to each station on every position update.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { BASE_URL } from '@/env';
import type { Station } from '@/types';

export interface GeoLocation {
  coords: {
    latitude: number;
    longitude: number;
    accuracy?: number;
  };
  is_moving?: boolean;
}

interface UseBackgroundGeoReturn {
  location: GeoLocation | null;
  setLocation: (loc: GeoLocation) => void;
  startTracking: (token: string, stations: Station[]) => Promise<void>;
  onGeofenceExit: (handler: ((stationId: number) => void) | null) => void;
}

export function haversineMetres(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6_371_000;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export function useBackgroundGeo(): UseBackgroundGeoReturn {
  const [location, setLocationState] = useState<GeoLocation | null>(null);
  const watchIdRef = useRef<number | null>(null);
  const insideStationsRef = useRef<Set<number>>(new Set());
  const onGeofenceExitRef = useRef<((stationId: number) => void) | null>(null);
  const lastSentRef = useRef<{ lat: number; lon: number } | null>(null);
  const mountedRef = useRef(true);

  // Starts true because a caller inside the provider may call startTracking from its own mount
  // effect, which runs before this one. The effect still sets it again after StrictMode's
  // simulated unmount.
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (watchIdRef.current !== null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
    };
  }, []);

  const setLocation = useCallback((loc: GeoLocation) => {
    setLocationState(loc);
  }, []);

  const startTracking = useCallback(async (token: string, stations: Station[]): Promise<void> => {
    // A caller can resolve after logout unmounts the hook, and nothing would clear that watch
    if (!navigator.geolocation || !mountedRef.current) return;

    // One watch at a time, so a second call doesn't post each location twice
    if (watchIdRef.current !== null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }

    watchIdRef.current = navigator.geolocation.watchPosition(
      pos => {
        const loc: GeoLocation = {
          coords: {
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
            accuracy: pos.coords.accuracy,
          },
          is_moving: (pos.coords.speed ?? 0) > 0.5,
        };

        setLocationState(loc);

        // Only POST to API when position has actually changed
        const last = lastSentRef.current;
        const lat = pos.coords.latitude;
        const lon = pos.coords.longitude;
        if (!last || last.lat !== lat || last.lon !== lon) {
          lastSentRef.current = { lat, lon };
          void fetch(`${BASE_URL}location`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(loc),
          }).catch(() => undefined);
        }

        // Geofence exit approximation: were we inside a 100 m radius, now outside?
        for (const station of stations) {
          const dist = haversineMetres(
            pos.coords.latitude,
            pos.coords.longitude,
            parseFloat(station.lat),
            parseFloat(station.lon),
          );
          const wasInside = insideStationsRef.current.has(station.id);
          if (dist <= 100 && !wasInside) {
            insideStationsRef.current.add(station.id);
          } else if (dist > 100 && wasInside) {
            insideStationsRef.current.delete(station.id);
            onGeofenceExitRef.current?.(station.id);
          }
        }
      },
      err => console.warn('Geolocation error', err),
      { enableHighAccuracy: true },
    );
  }, []);

  // Replaces bgGeo.onGeofence in app.js: the page registers what to do when the driver leaves a station
  const onGeofenceExit = useCallback((handler: ((stationId: number) => void) | null) => {
    onGeofenceExitRef.current = handler;
  }, []);

  return { location, setLocation, startTracking, onGeofenceExit };
}
